import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface CustomDomainRecord {
  domain: string;
  tenantId: string;
  spaceId: string;
  tenantSlug: string;
  spaceSlug: string;
  cfDistributionId: string | null;
  cfDomainName: string | null;
  acmCertificateArn: string | null;
  status: string;
  dnsValidationName: string | null;
  dnsValidationValue: string | null;
  redirectMode: string;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useCustomDomain(spaceId: string | undefined) {
  return useQuery<CustomDomainRecord | null>({
    queryKey: ['custom-domain', spaceId],
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/cms/spaces/${spaceId}/custom-domain`);
      return data.customDomain ?? null;
    },
    enabled: !!spaceId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'pending_validation' || status === 'creating_distribution') return 30_000;
      return false;
    },
  });
}

export function useSetupCustomDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ spaceId, domain, redirectMode }: { spaceId: string; domain: string; redirectMode?: string }) => {
      const { data } = await api.post(`/api/v1/cms/spaces/${spaceId}/custom-domain`, { domain, redirectMode });
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['custom-domain', vars.spaceId] });
    },
  });
}

export function useVerifyCustomDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (spaceId: string) => {
      const { data } = await api.post(`/api/v1/cms/spaces/${spaceId}/custom-domain/verify`);
      return data;
    },
    onSuccess: (_data, spaceId) => {
      qc.invalidateQueries({ queryKey: ['custom-domain', spaceId] });
    },
  });
}

export function useUpdateRedirectMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ spaceId, redirectMode }: { spaceId: string; redirectMode: string }) => {
      const { data } = await api.patch(`/api/v1/cms/spaces/${spaceId}/custom-domain`, { redirectMode });
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['custom-domain', vars.spaceId] });
    },
  });
}

export function useRemoveCustomDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (spaceId: string) => {
      const { data } = await api.delete(`/api/v1/cms/spaces/${spaceId}/custom-domain`);
      return data;
    },
    onSuccess: (_data, spaceId) => {
      qc.invalidateQueries({ queryKey: ['custom-domain', spaceId] });
    },
  });
}
